/**
 *  Modal
 * @author
 *
 * @copyright Klipfolio, All rights reserved
 */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { useCookies } from 'react-cookie'
import { useWindowWidth } from '@breakpoints'

const propTypes = {}

const CloseButton = styled.button`
  background: #fff;
  color: #3d455c;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  font-size: 1.5rem;
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  -webkit-appearance: none;
  box-shadow: 0px 4px 15px rgb(0 0 0 / 15%);
  transition: box-shadow 0.25s ease;
  transition-property: box-shadow;
  transition-duration: 0.25s;
  transition-timing-function: ease;
  transition-delay: 0s;
  text-transform: lowercase;
  margin-top: 0;
  padding: 0;
  border: 0;
  cursor: pointer;

  :hover {
    box-shadow: 0px 4px 16px rgb(255 255 255 / 20%);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`

const StyledModalContainer = styled.div`
  display: flex;
  position: fixed;
  visibility: ${({ open }) => (open ? 'visible' : 'hidden')};
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.85);
  z-index: 200;
  justify-content: center;
  align-items: center;
  opacity: ${({ open }) => (open ? '1' : '0')};
  transition: opacity linear 250ms;
`

const StyledModalContent = styled.div`
  z-index: 201;
`

const ModalComponent = ({
  className,
  children,
  open = false,
  delay = false,
  cookie,
  expires = 2,
  closeOnWidth,
  showButton = true,
  ...rest
}) => {
  const [isOpen, setOpen] = useState(open)
  const timeout = useRef(null)
  const [cookies, setCookie] = useCookies([cookie])
  const windowWidth = useWindowWidth()

  const closeModal = useCallback(() => {
    if (isOpen) {
      setOpen(false)
    }
  }, [isOpen, setOpen])

  useEffect(() => {
    if (closeOnWidth && windowWidth < closeOnWidth) {
      setOpen(false)
    }
  }, [setOpen, windowWidth, closeOnWidth])

  useEffect(() => {
    if (delay && !open && !timeout.current && windowWidth > closeOnWidth) {
      if (!cookies[cookie]) {
        timeout.current = setTimeout(() => {
          setOpen(true)
          setCookie(cookie, true, {
            expires: new Date(Date.now() + expires * 24 * 60 * 60 * 1000),
            path: '/'
          })
        }, delay)
      }
    }
  }, [
    delay,
    open,
    timeout,
    windowWidth,
    closeOnWidth,
    setOpen,
    setCookie,
    cookie
  ])

  useEffect(() => {
    return () => {
      if (timeout.current) {
        clearTimeout(timeout.current)
      }
    }
  }, [timeout])

  useEffect(() => {
    setOpen(open)
  }, [setOpen, open])

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'visible'
    }
  }, [isOpen])

  return (
    <StyledModalContainer open={isOpen}>
      <div {...rest} className={className}>
        {showButton && (
          <CloseButton onClick={closeModal}>
            <svg>
              <svg
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
              >
                <path
                  d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"
                  fill="#3d455c"
                />
              </svg>
            </svg>
          </CloseButton>
        )}
        <StyledModalContent>{children}</StyledModalContent>
      </div>
    </StyledModalContainer>
  )
}

ModalComponent.propTypes = {
  delay: PropTypes.bool,
  cookie: PropTypes.string,
  expires: PropTypes.string,
  closeOnWidth: PropTypes.bool,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]),
  className: PropTypes.string,
  open: PropTypes.bool,
  showButton: PropTypes.bool
}

const Modal = styled(ModalComponent)`
  position: relative;
  display: block;
  box-shadow: 0 5px 15px rgb(0 0 0 / 50%);
  max-width: 700px;
  border-radius: 1rem;
`

Modal.propTypes = propTypes
export default Modal
